import Button from "@/components/common/Button";
import Card from "@/components/common/Card";
import { PencilIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { getInitials } from "@/utils/formatters";
import { API_BASE_URL } from "@/lib/axios";

interface PersonalInfoTabProps {
  user: any;
  companyData?: any;
  isEditing: boolean;
  formData: {
    fullName: string;
    email: string;
    phoneNumber: string;
    photo: string;
    logo: string;
  };
  companyName: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  getImageUrl: (photo: string) => string;
  onEditClick: () => void;
  onCancelEdit: () => void;
  onFormDataChange: (field: string, value: string) => void;
  onCompanyNameChange: (value: string) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileInputClick: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
}

const PersonalInfoTab = ({
  user,
  companyData,
  isEditing,
  formData,
  companyName,
  fileInputRef,
  getImageUrl,
  onEditClick,
  onCancelEdit,
  onFormDataChange,
  onCompanyNameChange,
  onFileChange,
  onFileInputClick,
  onSubmit,
  isSubmitting,
}: PersonalInfoTabProps) => {
  const { t } = useTranslation();

  // Get user initials safely
  const getUserInitials = () => {
    return getInitials(user?.fullName || "");
  };

  // Get profile image URL for regular users (same logic as navbar)
  const getProfileImageUrl = (): string | undefined => {
    if (user?.photo) {
      // If it's a data URL (base64), use it directly
      if (user.photo.startsWith("data:")) {
        return user.photo;
      }
      // If it's a filename, construct the API URL
      return `${user.photo}`;
    }
    return undefined;
  };


  return (
    <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
      {isEditing ? (
        <form onSubmit={onSubmit}>
          <div className="p-6">
            <h3 className="mb-6 text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent dark:from-white dark:to-gray-300">
              {t("profile.editPersonalInfo")}
            </h3>

            <div className="space-y-6">
              {/* Logo Upload for Company Admins, Photo Upload for Regular Users */}
              <div className="flex items-center space-x-6">
                <div className="relative">
                  {user?.role === "company_admin" && companyData ? (
                    // Company admin - show company logo
                    formData.logo && formData.logo !== "default.jpg" && !formData.logo.includes("default-logo") ? (
                      <img
                        src={formData.logo.startsWith('data:') ? formData.logo : getImageUrl(formData.logo)}
                        alt={t("profile.companyLogo")}
                        className="h-24 w-24 rounded-full object-cover"
                        onError={(e) => {
                          // If image fails to load, show default avatar
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null
                  ) : (
                    // Regular user - show profile photo (same logic as navbar)
                    formData.photo ? (
                      <img
                        src={formData.photo.startsWith('data:') ? formData.photo : getImageUrl(formData.photo)}
                        alt={t("profile.profile")}
                        className="h-24 w-24 rounded-full object-cover"
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null
                  )}
                  <div className={`flex h-24 w-24 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 ${
                    user?.role === "company_admin" && companyData 
                      ? (formData.logo && formData.logo !== "default.jpg" && !formData.logo.includes("default-logo") ? 'hidden' : '')
                      : (formData.photo ? 'hidden' : '')
                  }`}>
                    {user?.role === "company_admin" && companyData ? (
                      <PhotoIcon className="h-8 w-8 text-gray-400" />
                    ) : (
                      <span className="text-lg font-bold text-gray-600 dark:text-gray-300">
                        {getUserInitials()}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onFileInputClick}
                    className="flex items-center space-x-2"
                  >
                    <PhotoIcon className="h-4 w-4" />
                    <span>{user?.role === "company_admin" && companyData ? t("profile.changeLogo") : t("profile.changePhoto")}</span>
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onFileChange}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t("profile.fileSizeLimit", "Max file size: 2MB. Images will be automatically compressed.")}
                  </p>
                </div>
              </div>

              {/* Personal Information Fields */}
              {user?.role === "company_admin" ? (
                // Company admin - show only email and phone (read-only)
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.email")}
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      disabled
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 px-3 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t("profile.emailReadOnly", "Email cannot be changed")}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.phoneNumber")}
                    </label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      disabled
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 px-3 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t("profile.phoneReadOnly", "Phone number cannot be changed")}
                    </p>
                  </div>
                </div>
              ) : (
                // Regular user - show full name and read-only email/phone
                <div className="grid grid-cols-1 gap-6 md:grid-cols-1">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.fullName")}
                    </label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => onFormDataChange("fullName", e.target.value)}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      required
                    />
                  </div>

                  {/* Read-only fields for email and phone */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("profile.email")}
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 px-3 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("profile.emailReadOnly", "Email cannot be changed")}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("profile.phoneNumber")}
                      </label>
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        disabled
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 px-3 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("profile.phoneReadOnly", "Phone number cannot be changed")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Company Information (for company admins) */}
              {companyData && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t("profile.companyInfo")}
                  </h4>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("profile.companyName")}
                      </label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => onCompanyNameChange(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    
                    {/* Read-only company email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("profile.companyEmail")}
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        disabled
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 px-3 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("profile.companyEmailReadOnly", "Company email cannot be changed")}
                      </p>
                    </div>
                  </div>
                  
                  {/* Read-only company phone */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.companyPhone")}
                    </label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      disabled
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-600 px-3 py-2 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t("profile.companyPhoneReadOnly", "Company phone cannot be changed")}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancelEdit}
                  disabled={isSubmitting}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  className="flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>{t("common.saving")}</span>
                    </>
                  ) : (
                    <>
                      <PencilIcon className="h-4 w-4" />
                      <span>{t("common.save")}</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent dark:from-white dark:to-gray-300">
              {t("profile.personalInfo")}
            </h3>
            <Button
              onClick={onEditClick}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <PencilIcon className="h-4 w-4" />
              <span>{t("common.edit")}</span>
            </Button>
          </div>

            <div className="space-y-6">
              {/* Current Logo for Company Admins, Profile Photo for Regular Users */}
              <div className="flex items-center space-x-6">
                <div className="relative">
                  {user?.role === "company_admin" && companyData ? (
                    // Company admin - show company logo
                    companyData.logo && companyData.logo !== "default.jpg" && !companyData.logo.includes("default-logo") ? (
                      <img
                        src={getImageUrl(companyData.logo)}
                        alt={t("profile.companyLogo")}
                        className="h-24 w-24 rounded-full object-cover shadow-lg"
                        onError={(e) => {
                          // If image fails to load, show default avatar
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null
                  ) : (
                    // Regular user - show profile photo (same logic as navbar)
                    getProfileImageUrl() && (
                      <img
                        src={getProfileImageUrl()}
                        alt={t("profile.profile")}
                        className="h-24 w-24 rounded-full object-cover shadow-lg"
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          target.nextElementSibling?.classList.remove("hidden");
                        }}
                      />
                    )
                  )}
                  <div className={`flex h-24 w-24 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 ${
                    user?.role === "company_admin" && companyData 
                      ? (companyData.logo && companyData.logo !== "default.jpg" && !companyData.logo.includes("default-logo") ? 'hidden' : '')
                      : (getProfileImageUrl() ? 'hidden' : '')
                  }`}>
                    {user?.role === "company_admin" && companyData ? (
                      <PhotoIcon className="h-8 w-8 text-gray-400" />
                    ) : (
                      <span className="text-lg font-bold text-gray-600 dark:text-gray-300">
                        {getUserInitials()}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {user?.role === "company_admin" && companyData 
                      ? t("profile.currentLogo")
                      : t("profile.profilePhoto")
                    }
                  </p>
                </div>
              </div>

            {/* Personal Information Display */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {user?.role === "company_admin" ? (
                // Company admin - show different fields
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.email")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{user?.email || t("profile.notProvided")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.phoneNumber")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{user?.phoneNumber || t("profile.notProvided")}</p>
                  </div>
                </>
              ) : (
                // Regular user - show all fields including full name
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.fullName")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{user?.fullName || t("profile.notProvided")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.email")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{user?.email || t("profile.notProvided")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.phoneNumber")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{user?.phoneNumber || t("profile.notProvided")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.role")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{user?.role || t("profile.notProvided")}</p>
                  </div>
                </>
              )}
            </div>

            {/* Company Information Display (for company admins) */}
            {companyData && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t("profile.companyInfo")}
                </h4>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.companyName")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{companyData.name || t("profile.notProvided")}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("profile.industry")}
                    </label>
                    <p className="text-gray-900 dark:text-white">{companyData.industry || t("profile.notProvided")}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default PersonalInfoTab;
